import { useEffect, useState } from 'react';

/**
 * Which surface are we on, and what proves who the user is?
 *
 * Resolution order — deliberately in this order, and deliberately done with the
 * raw `window.Telegram.WebApp` global rather than `@telegram-apps/sdk`:
 *
 *   1. `window.Telegram?.WebApp?.initData` -> `{ mode: 'telegram' }`
 *   2. `sessionStorage.tg_login`           -> `{ mode: 'browser' }`
 *   3. otherwise                           -> `{ mode: 'unauthenticated' }`
 *
 * `@telegram-apps/sdk` v3+ is ESM-only and *throws* when it cannot find a Mini
 * Apps environment, so the probe has to run before the SDK is initialised.
 * See `src/telegram/sdk.ts` — nothing there is imported until this resolves to
 * `telegram`.
 */

export const LOGIN_STORAGE_KEY = 'tg_login';

export interface TelegramLoginUser {
  id: number;
  first_name: string;
  last_name?: string;
  username?: string;
  photo_url?: string;
  auth_date: number;
  hash: string;
}

export type TelegramContext =
  | {
      mode: 'telegram';
      /** Raw query string; goes out as the `X-Telegram-Init-Data` header. */
      initData: string;
      /** `ios`, `android`, `tdesktop`, `macos`, `weba`, ... */
      platform: string;
      colorScheme: 'light' | 'dark';
    }
  | {
      mode: 'browser';
      /** Serialised Login Widget payload; goes out as `X-Telegram-Login-Data`. */
      authData: string;
      user: TelegramLoginUser;
    }
  | { mode: 'unauthenticated' };

/** The slice of the Telegram WebApp global we actually touch. */
interface TelegramWebAppLike {
  initData?: string;
  platform?: string;
  colorScheme?: 'light' | 'dark';
  themeParams?: Record<string, string>;
  ready?: () => void;
  expand?: () => void;
  onEvent?: (event: string, cb: () => void) => void;
  offEvent?: (event: string, cb: () => void) => void;
  /** Hands a URL to the host so it opens outside the Mini App's webview. */
  openLink?: (url: string) => void;
}

declare global {
  interface Window {
    Telegram?: { WebApp?: TelegramWebAppLike };
  }
}

export function getTelegramWebApp(): TelegramWebAppLike | undefined {
  if (typeof window === 'undefined') return undefined;
  return window.Telegram?.WebApp;
}

/** True when a Telegram Mini App host handed us a signed `initData`. */
export function isTelegramSurface(): boolean {
  const webApp = getTelegramWebApp();
  return typeof webApp?.initData === 'string' && webApp.initData.length > 0;
}

function readStoredLogin(): TelegramLoginUser | null {
  try {
    const raw = sessionStorage.getItem(LOGIN_STORAGE_KEY);
    if (!raw) return null;
    const parsed = JSON.parse(raw) as TelegramLoginUser;
    if (typeof parsed?.id !== 'number' || typeof parsed?.hash !== 'string') return null;
    return parsed;
  } catch {
    // Private-mode Safari throws on sessionStorage access; treat as signed out.
    return null;
  }
}

/** Serialisation the API middleware expects for `X-Telegram-Login-Data`. */
export function serialiseLoginData(user: TelegramLoginUser): string {
  return Object.entries(user)
    .filter(([, v]) => v !== undefined && v !== null)
    .map(([k, v]) => `${encodeURIComponent(k)}=${encodeURIComponent(String(v))}`)
    .join('&');
}

/** Pure, synchronous, safe to call before React mounts. */
export function resolveTelegramContext(): TelegramContext {
  const webApp = getTelegramWebApp();
  const initData = webApp?.initData;
  if (typeof initData === 'string' && initData.length > 0) {
    return {
      mode: 'telegram',
      initData,
      platform: webApp?.platform ?? 'unknown',
      colorScheme: webApp?.colorScheme ?? 'light',
    };
  }

  const user = readStoredLogin();
  if (user) {
    return { mode: 'browser', authData: serialiseLoginData(user), user };
  }

  return { mode: 'unauthenticated' };
}

/** Module-level cache so the API client can read it without a React render. */
let current: TelegramContext = resolveTelegramContext();
const listeners = new Set<(ctx: TelegramContext) => void>();

export function getTelegramContext(): TelegramContext {
  return current;
}

function publish(next: TelegramContext) {
  current = next;
  listeners.forEach((fn) => fn(next));
}

/** Called by the auth screen once the Login Widget returns a signed payload. */
export function signInWithLoginWidget(user: TelegramLoginUser) {
  try {
    sessionStorage.setItem(LOGIN_STORAGE_KEY, JSON.stringify(user));
  } catch {
    /* storage unavailable — the session stays in memory only */
  }
  publish({ mode: 'browser', authData: serialiseLoginData(user), user });
}

export function signOut() {
  try {
    sessionStorage.removeItem(LOGIN_STORAGE_KEY);
  } catch {
    /* ignore */
  }
  publish(resolveTelegramContext());
}

export function useTelegramContext(): TelegramContext {
  const [ctx, setCtx] = useState<TelegramContext>(current);
  useEffect(() => {
    listeners.add(setCtx);
    // Re-probe on mount: the WebApp global can land after the module evaluates.
    const fresh = resolveTelegramContext();
    if (fresh.mode !== current.mode) publish(fresh);
    else setCtx(current);
    return () => {
      listeners.delete(setCtx);
    };
  }, []);
  return ctx;
}
